import { observer } from 'mobx-react-lite'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useStore } from '~/app/store'
import { OrderData } from '~/shared/api/services/warehouses/types'
import { PageTitle } from '~/shared/ui'
import { FilterSVG } from '~/shared/ui/SVG/SVGFilter'
import Spinner from '~/shared/ui/spinner'
import { NotFound } from '~/pages/not-found'
import GoToTop from '../scroll-to-top'
import { AssemblyDateFilters } from './date-filters'
import { ExpressOrdersSection } from './express-orders-section'
import { AssemblyOrderCard } from './order-card'
import { OrdersByDateSection } from './orders-by-date-section'
import {
  adaptiveFontSize,
  getExpressOrders,
  getRegularOzonOrders,
} from './utils'

export const AssemblyByType = observer(() => {
  const { t } = useTranslation(['assembly'])
  const { warehousesStore } = useStore()
  const { getAssembly, assembly, loaders } = warehousesStore
  const { type } = useParams<{ type: string }>()
  const [isOpen, setIsOpen] = useState(false)
  const dateRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})
  const navigate = useNavigate()

  useEffect(() => {
    if (type && type !== 'all') {
      getAssembly(type)
    }
  }, [type, getAssembly])

  if (type === 'all') {
    return <NotFound />
  }

  const toggleFilter = () => {
    setIsOpen((prev) => !prev)
  }

  const openOrder = (order: OrderData) => {
    warehousesStore.setSelectedOrder(order)
    navigate(`/assembly/${order.id}/items`)
  }

  useEffect(() => {
    const scrollToDate = (date: string) => {
      const targetRef = dateRefs.current[date]
      if (targetRef) {
        targetRef.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (target && target.id) {
        scrollToDate(target.id)
      }

      if (
        !target.closest('.filter-button') &&
        !target.closest('.filter-content')
      ) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  if (loaders.assembly) {
    return (
      <div className="flex justify-center items-center h-[80vh]">
        <Spinner />
      </div>
    )
  }

  const dateFilterDates = assembly?.ordersData?.dates
    ? (type === 'ozon'
        ? getRegularOzonOrders(assembly.ordersData.dates)
        : assembly.ordersData.dates
      ).map(({ date }) => date)
    : []

  const renderOrdersByType = () => {
    if (!assembly?.ordersData?.dates) {
      return <Spinner />
    }

    const { dates } = assembly.ordersData

    if (type === 'platform' || type === 'retail') {
      const orders = dates.reduce(
        (acc, current) => [...acc, ...current.orders],
        [] as OrderData[],
      )

      return (
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-5">
          {orders.length !== 0 ? (
            orders.map((order) => (
              <AssemblyOrderCard
                key={order.id}
                order={order}
                className="w-[140px] h-[50px] text-center"
                adaptiveFontSize={adaptiveFontSize}
                onOpen={openOrder}
              />
            ))
          ) : (
            <p>{t('assembly:noOrders')}</p>
          )}
        </div>
      )
    }

    const expressOrders = type === 'ozon' ? getExpressOrders(dates) : []

    const regularOzonOrders =
      type === 'ozon' ? getRegularOzonOrders(dates) : dates

    let regularOrdersContent: React.ReactNode = null

    if (regularOzonOrders.length !== 0) {
      regularOrdersContent = (
        <OrdersByDateSection
          dates={regularOzonOrders}
          dateRefs={dateRefs}
          adaptiveFontSize={adaptiveFontSize}
          onOpenOrder={openOrder}
        />
      )
    } else if (type !== 'ozon') {
      regularOrdersContent = <p>{t('assembly:noOrders')}</p>
    }

    return (
      <div className="flex flex-col">
        {type === 'ozon' ? (
          <ExpressOrdersSection
            orders={expressOrders}
            adaptiveFontSize={adaptiveFontSize}
            onOpenOrder={openOrder}
          />
        ) : null}

        {regularOrdersContent}
      </div>
    )
  }

  const renderDateFilters = () => {
    return <AssemblyDateFilters dates={dateFilterDates} />
  }

  return (
    <>
      <div className="relative">
        <div className="flex justify-between items-center mb-6">
          <div className="min-w-[200px]">
            {assembly ? (
              <PageTitle
                withBackButton
                title={t('assembly:assemblyTitle', { type })}
                size="text-[24px]"
              />
            ) : (
              <div className="h-[32px] w-full" />
            )}
          </div>

          {type !== 'platform' &&
          type !== 'retail' &&
          dateFilterDates.length !== 0 ? (
            <div
              className="filter-button w-[32px] h-[32px] bg-white border border-gray-200 flex justify-center items-center rounded-lg cursor-pointer"
              onClick={toggleFilter}
            >
              <FilterSVG size="24px" color="#242424" />
            </div>
          ) : (
            <div className="w-[32px] h-[32px]" />
          )}
        </div>

        {renderOrdersByType()}
      </div>
      <GoToTop />
      {isOpen && renderDateFilters()}
    </>
  )
})
